<script setup lang="ts">
import { MapboxMap, MapboxMarker, MapboxLayer } from '@studiometa/vue-mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { notifyError } from '@/utils/helpers'

definePage({
    meta: {
        id: 'prsm.capteurs',
        icon: 'mdi-antenna',
        title: 'Capteurs de spores',
        drawerIndex: 99,
        requiresAuth: true,
        requiresSiteAccess: 'prisme',
    },
})

const theme = useTheme()
const display = useDisplay()
const storeU = useUtilisateurStore()
const dialogConfirm = useTemplateRef('dialogConfirm')

const MAPBOX_TOKEN = 'pk.eyJ1IjoiYWdyaW1ldHJpZSIsImEiOiJjbTc3cjhrMTgxZGNqMnFxOGM4aGpjaW5hIn0.fUMzok-3aNnM9Zg7hpBKjQ'

// ── State ────────────────────────────────────────────────────────────────
const isLoading = ref(false)
const isSaving = ref(false)
const canEdit = computed(() => storeU.aAccèsPermission('site.PRSM.put.prsm.capture.capteurs'))
const siteCS = computed(() => storeU.siteSélectionné.lettresDappel)
const culturesDisponibles = ['Oignon', 'Laitue', 'Pomme de terre', 'Vigne', 'Crucifère']

const capteurs = ref<MDBPrismeCaptureCapteur[]>([])
const filter = ref('')
const showDésactivés = ref(false)

const filteredCapteurs = computed(() => {
    const q = filter.value.trim().toLowerCase()
    const list = showDésactivés.value ? capteurs.value : capteurs.value.filter(c => !c.désactivé)
    if (!q) return list
    return list.filter(c =>
        c.nom.toLowerCase().includes(q) ||
        c.thingsBoardId.toLowerCase().includes(q) ||
        c.dernierChampNom.toLowerCase().includes(q) ||
        c.dernierClientNom.toLowerCase().includes(q)
    )
})

// ── Dialog ────────────────────────────────────────────────────────────────
const showDialog = ref(false)
const isEditMode = ref(false)
const isSavingDialog = ref(false)
const isLocating = ref(false)

declare interface CapteurForm {
    id: string
    thingsBoardId: string
    nom: string
    dernièreLatitude: number | null
    dernièreLongitude: number | null
    dernierChampId: string
    dernierChampNom: string
    dernierRangOrdre: number
    désactivé: boolean
    cultures: string[]
}

const emptyForm = (): CapteurForm => ({
    id: '',
    thingsBoardId: '',
    nom: '',
    dernièreLatitude: null,
    dernièreLongitude: null,
    dernierChampId: '',
    dernierChampNom: '',
    dernierRangOrdre: 0,
    désactivé: false,
    cultures: [],
})

const form = ref<CapteurForm>(emptyForm())

// ── Clients / Champ autocomplete ──────────────────────────────────────────
const clients = ref<string[]>([])
const selectedClient = ref<string | null>(null)
const champPhrase = ref('')
const champSuggestions = ref<MDBPrismeChamp[]>([])
const selectedChamp = ref<MDBPrismeChamp | null>(null)
const champValue = ref<MDBPrismeChamp | string | null>(null)
const isLoadingChamps = ref(false)
// Tracks whether the current lat/lng were auto-filled from a champ selection (vs. manually set)
const coordsFromChamp = ref(false)

async function loadClients() {
    if (clients.value.length > 0) return
    try {
        const res = await apiProphet.get(`prsm/${siteCS.value}/clients`)
        clients.value = res.data as string[]
    } catch (e) {
        notifyError(e)
    }
}

let champSearchTimeout: ReturnType<typeof setTimeout> | null = null
async function onChampInput(phrase: string | null) {
    if (!phrase) { champSuggestions.value = []; return }
    champPhrase.value = phrase
    champSuggestions.value = []
    if (!selectedClient.value || phrase.trim().length < 1) return
    if (champSearchTimeout) clearTimeout(champSearchTimeout)
    champSearchTimeout = setTimeout(async () => {
        isLoadingChamps.value = true
        try {
            const res = await apiProphet.get(`prsm/${siteCS.value}/champs/completion`, {
                params: { clientLD: selectedClient.value, phrase },
            })
            champSuggestions.value = res.data as MDBPrismeChamp[]
        } catch (e) {
            notifyError(e)
        } finally {
            isLoadingChamps.value = false
        }
    }, 300)
}

function onChampSelect(value: MDBPrismeChamp | string | null) {
    champValue.value = value
    if (!value) {
        selectedChamp.value = null
        form.value.dernierChampId = ''
        form.value.dernierChampNom = ''
    } else if (typeof value === 'string') {
        // Free-text entry — no champ ID; clear any coordinates that were auto-filled from the previous champ
        if (coordsFromChamp.value) {
            form.value.dernièreLatitude = null
            form.value.dernièreLongitude = null
            coordsFromChamp.value = false
        }
        selectedChamp.value = null
        form.value.dernierChampId = ''
        form.value.dernierChampNom = value
    } else {
        selectedChamp.value = value
        form.value.dernierChampId = value.id
        form.value.dernierChampNom = value.nom
        if (value.latitude !== 0 || value.longitude !== 0) {
            form.value.dernièreLatitude = value.latitude
            form.value.dernièreLongitude = value.longitude
            mapCenter.value = [value.longitude, value.latitude]
            coordsFromChamp.value = true
        }
    }
}

watch(selectedClient, (newClient) => {
    if (selectedChamp.value?.sitePrsmLD === newClient) return
    selectedChamp.value = null
    champValue.value = null
    champPhrase.value = ''
    champSuggestions.value = []
    form.value.dernierChampId = ''
    form.value.dernierChampNom = ''
    if (coordsFromChamp.value) {
        form.value.dernièreLatitude = null
        form.value.dernièreLongitude = null
        coordsFromChamp.value = false
    }
})

// ── Map ──────────────────────────────────────────────────────────────────
const mapCenter = ref<[number, number]>([-73.5, 46.5])
const isLocatingNearby = ref(false)

// ── Champs GeoJSON overlay ───────────────────────────────────────────────
const showChampsOverlay = ref(false)
const isLoadingGeoJson = ref(false)
const champsGeoJson = ref<any>(null)

const producerColors = [
    '#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336',
    '#00BCD4', '#E91E63', '#8BC34A', '#3F51B5', '#FF5722',
    '#009688', '#673AB7', '#CDDC39', '#795548', '#607D8B',
    '#FFC107', '#03A9F4', '#76FF03', '#FF4081', '#536DFE',
]

const producerColorMap = computed(() => {
    const map = new Map<string, string>()
    if (!champsGeoJson.value) return map
    const unique = [...new Set(
        champsGeoJson.value.features
            .map((f: any) => f.properties?.fast_carto)
            .filter((v: any) => v != null),
    )] as string[]
    unique.forEach((producer, i) => map.set(producer, producerColors[i % producerColors.length]))
    return map
})

function buildFillColorExpr() {
    const entries = [...producerColorMap.value.entries()]
    if (entries.length === 0) return '#FFEB3B'
    const expr: any[] = ['match', ['get', 'fast_carto']]
    for (const [key, color] of entries) expr.push(key, color)
    expr.push('#FFEB3B')
    return expr
}

function buildOutlineColorExpr() {
    const entries = [...producerColorMap.value.entries()]
    if (entries.length === 0) return '#F57F17'
    const expr: any[] = ['match', ['get', 'fast_carto']]
    for (const [key, color] of entries) expr.push(key, color)
    expr.push('#F57F17')
    return expr
}

const champsLabelsGeoJson = computed(() => {
    if (!champsGeoJson.value) return { type: 'FeatureCollection', features: [] }
    const features = champsGeoJson.value.features.map((feature: any) => {
        const coords = feature.geometry?.coordinates
        if (!coords?.length) return null
        const ring = feature.geometry.type === 'MultiPolygon' ? coords[0][0] : coords[0]
        if (!ring?.length) return null
        let sumX = 0, sumY = 0
        for (const pt of ring) { sumX += pt[0]; sumY += pt[1] }
        const label = feature.properties?.IDENCHAMP || ''
        if (!label) return null
        return {
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [sumX / ring.length, sumY / ring.length] },
            properties: { IDENCHAMP: label, fast_carto: feature.properties?.fast_carto ?? null },
        }
    }).filter(Boolean)
    return { type: 'FeatureCollection', features }
})

const champsGroupLabelsGeoJson = computed(() => {
    if (!champsGeoJson.value) return { type: 'FeatureCollection', features: [] }
    const groups = new Map<string, number[][]>()
    for (const feature of champsGeoJson.value.features) {
        const producer = feature.properties?.fast_carto
        if (!producer) continue
        const coords = feature.geometry?.coordinates
        if (!coords?.length) continue
        const ring = feature.geometry.type === 'MultiPolygon' ? coords[0][0] : coords[0]
        if (!ring?.length) continue
        let sumX = 0, sumY = 0
        for (const pt of ring) { sumX += pt[0]; sumY += pt[1] }
        if (!groups.has(producer)) groups.set(producer, [])
        groups.get(producer)!.push([sumX / ring.length, sumY / ring.length])
    }
    const features = [...groups.entries()].map(([producer, centroids]) => ({
        type: 'Feature',
        geometry: {
            type: 'Point',
            coordinates: [
                centroids.reduce((s, c) => s + c[0], 0) / centroids.length,
                centroids.reduce((s, c) => s + c[1], 0) / centroids.length,
            ],
        },
        properties: { fast_carto: producer },
    }))
    return { type: 'FeatureCollection', features }
})

const emptyFc = { type: 'FeatureCollection', features: [] }

const champsFillLayerOptions = computed(() => ({
    type: 'fill',
    source: { type: 'geojson', data: champsGeoJson.value ?? emptyFc },
    paint: { 'fill-color': buildFillColorExpr(), 'fill-opacity': 0.35, 'fill-outline-color': buildOutlineColorExpr() },
}))

const champsOutlineLayerOptions = computed(() => ({
    type: 'line',
    source: { type: 'geojson', data: champsGeoJson.value ?? emptyFc },
    paint: { 'line-color': buildOutlineColorExpr(), 'line-width': 1.5 },
}))

const champsLabelLayerOptions = computed(() => ({
    type: 'symbol',
    source: { type: 'geojson', data: champsLabelsGeoJson.value },
    minzoom: 12,
    layout: {
        'text-field': ['get', 'IDENCHAMP'],
        'text-size': 11,
        'text-anchor': 'center',
        'text-allow-overlap': false,
        'text-optional': true,
    },
    paint: { 'text-color': '#000000', 'text-halo-color': '#FFFFFF', 'text-halo-width': 1.5 },
}))

const champsGroupLabelLayerOptions = computed(() => {
    const entries = [...producerColorMap.value.entries()]
    let textColor: any = '#FFFFFF'
    if (entries.length > 0) {
        textColor = ['match', ['get', 'fast_carto']]
        for (const [key, color] of entries) textColor.push(key, color)
        textColor.push('#FFFFFF')
    }
    return {
        type: 'symbol',
        source: { type: 'geojson', data: champsGroupLabelsGeoJson.value },
        layout: {
            'text-field': ['get', 'fast_carto'],
            'text-size': 14,
            'text-anchor': 'center',
            'text-allow-overlap': false,
            'text-optional': true,
            'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
            'text-letter-spacing': 0.05,
        },
        paint: { 'text-color': textColor, 'text-halo-color': 'rgba(0,0,0,0.75)', 'text-halo-width': 2 },
    }
})

async function toggleChampsOverlay() {
    showChampsOverlay.value = !showChampsOverlay.value
    if (showChampsOverlay.value && !champsGeoJson.value) {
        isLoadingGeoJson.value = true
        try {
            const res = await apiProphet.get(`prsm/${siteCS.value}/champs/geojson`)
            const jsons: any[] = Array.isArray(res.data) ? res.data : [res.data]
            const features: any[] = []
            for (const geojson of jsons) {
                if (geojson.type === 'FeatureCollection' && geojson.features)
                    features.push(...geojson.features)
                else if (geojson.type === 'Feature')
                    features.push(geojson)
            }
            champsGeoJson.value = { type: 'FeatureCollection', features }
        } catch (e) {
            notifyError(e)
            showChampsOverlay.value = false
        } finally {
            isLoadingGeoJson.value = false
        }
    }
}

async function findNearbyChamp(lat: number, lng: number) {
    try {
        const res = await apiProphet.get(`prsm/${siteCS.value}/champs/proche`, {
            params: { longitude: lng, latitude: lat },
        })
        const champ = res.data as MDBPrismeChamp
        if (champ?.id) {
            selectedClient.value = champ.sitePrsmLD
            champSuggestions.value = [champ]
            selectedChamp.value = champ
            champValue.value = champ
            champPhrase.value = champ.nom
            form.value.dernierChampId = champ.id
            form.value.dernierChampNom = champ.nom
            if (champ.latitude !== 0 || champ.longitude !== 0) {
                form.value.dernièreLatitude = champ.latitude
                form.value.dernièreLongitude = champ.longitude
                mapCenter.value = [champ.longitude, champ.latitude]
                coordsFromChamp.value = true
            }
        }
    } catch {
        // silently ignore — nearby champ is best-effort
    }
}

function onMapClick(e: any) {
    const { lng, lat } = e.lngLat
    form.value.dernièreLatitude = parseFloat(lat.toFixed(6))
    form.value.dernièreLongitude = parseFloat(lng.toFixed(6))
    coordsFromChamp.value = false

    // If the champs overlay is active, check if the click landed inside a polygon
    if (!showChampsOverlay.value) return
    const map = e.target
    if (!map) return
    const features = map.queryRenderedFeatures(e.point, { layers: ['capteur-champs-fill'] })
    if (!features?.length) return

    const props = features[0].properties
    const clientLD: string = props?.fast_carto ?? ''
    const champNom: string = props?.IDENCHAMP ?? ''
    const champId: string = props?.ChampId ?? ''
    const x: number = props?.X ?? 0
    const y: number = props?.Y ?? 0

    if (!champNom) return

    const champ: MDBPrismeChamp = {
        id: champId,
        siteId: '',
        sitePrsmLD: clientLD,
        nom: champNom,
        latitude: y,
        longitude: x,
        acres: props?.Acres ?? 0,
        hectares: props?.Hectares ?? 0,
        perimetre: props?.Perimetre ?? 0,
    }
    selectedClient.value = clientLD
    champSuggestions.value = [champ]
    selectedChamp.value = champ
    champValue.value = champ
    champPhrase.value = champNom
    form.value.dernierChampId = champId
    form.value.dernierChampNom = champNom
    if (x !== 0 || y !== 0) { mapCenter.value = [x, y]; coordsFromChamp.value = true }
}

function locateMe() {
    if (!navigator.geolocation) {
        notifyError('La géolocalisation n\'est pas supportée par ce navigateur.')
        return
    }
    isLocating.value = true
    navigator.geolocation.getCurrentPosition(
        (pos) => {
            form.value.dernièreLatitude = parseFloat(pos.coords.latitude.toFixed(6))
            form.value.dernièreLongitude = parseFloat(pos.coords.longitude.toFixed(6))
            mapCenter.value = [pos.coords.longitude, pos.coords.latitude]
            coordsFromChamp.value = false
            isLocating.value = false
        },
        (err) => {
            isLocating.value = false
            notifyError(`Impossible d'obtenir la position : ${err.message}`)
        },
        { enableHighAccuracy: true, timeout: 10000 }
    )
}

function autoLocateAndFillChamp() {
    if (!navigator.geolocation) return
    isLocatingNearby.value = true
    navigator.geolocation.getCurrentPosition(
        async (pos) => {
            const lat = parseFloat(pos.coords.latitude.toFixed(6))
            const lng = parseFloat(pos.coords.longitude.toFixed(6))
            form.value.dernièreLatitude = lat
            form.value.dernièreLongitude = lng
            mapCenter.value = [lng, lat]
            await findNearbyChamp(lat, lng)
            isLocatingNearby.value = false
        },
        () => { isLocatingNearby.value = false },
        { enableHighAccuracy: true, timeout: 8000 }
    )
}
const markerCoords = computed<[number, number] | null>(() => {
    if (form.value.dernièreLatitude !== null && form.value.dernièreLongitude !== null)
        return [form.value.dernièreLongitude, form.value.dernièreLatitude]
    return null
})

// ── CRUD ─────────────────────────────────────────────────────────────────
async function getData() {
    isLoading.value = true
    try {
        const res = await apiProphet.get(`prsm/${siteCS.value}/captures/capteurs`)
        capteurs.value = res.data as MDBPrismeCaptureCapteur[]
    } catch (e) {
        notifyError(e)
    } finally {
        isLoading.value = false
    }
}

function openCreate() {
    form.value = emptyForm()
    selectedClient.value = null
    selectedChamp.value = null
    champValue.value = null
    champPhrase.value = ''
    champSuggestions.value = []
    coordsFromChamp.value = false
    isEditMode.value = false
    showDialog.value = true
    loadClients()
    autoLocateAndFillChamp()
}

function openEdit(capteur: MDBPrismeCaptureCapteur) {
    form.value = {
        id: capteur.id,
        thingsBoardId: capteur.thingsBoardId,
        nom: capteur.nom,
        dernièreLatitude: capteur.dernièreLatitude || null,
        dernièreLongitude: capteur.dernièreLongitude || null,
        dernierChampId: capteur.dernierChampId,
        dernierChampNom: capteur.dernierChampNom,
        dernierRangOrdre: capteur.dernierRangOrdre,
        désactivé: capteur.désactivé ?? false,
        cultures: capteur.cultures ?? [],
    }
    if (capteur.dernièreLongitude && capteur.dernièreLatitude)
        mapCenter.value = [capteur.dernièreLongitude, capteur.dernièreLatitude]
    selectedClient.value = capteur.dernierClientNom || null
    // Coordinates are considered "from champ" only when a champ was previously associated
    coordsFromChamp.value = !!capteur.dernierChampId
    if (capteur.dernierChampId) {
        const champ: MDBPrismeChamp = {
            id: capteur.dernierChampId,
            siteId: '',
            sitePrsmLD: capteur.dernierClientNom,
            nom: capteur.dernierChampNom,
            latitude: capteur.dernièreLatitude ?? 0,
            longitude: capteur.dernièreLongitude ?? 0,
            acres: 0,
            hectares: 0,
            perimetre: 0,
        }
        champSuggestions.value = [champ]
        selectedChamp.value = champ
        champValue.value = champ
        champPhrase.value = capteur.dernierChampNom
    } else {
        selectedChamp.value = null
        champValue.value = capteur.dernierChampNom || null
        champPhrase.value = ''
        champSuggestions.value = []
    }
    isEditMode.value = true
    showDialog.value = true
    loadClients()
}

async function saveDialog() {
    if (!form.value.nom.trim()) {
        notifyError('Le nom est requis.')
        return
    }

    isSavingDialog.value = true
    try {
        const payload: MDBPrismeCaptureCapteur = {
            id: form.value.id,
            thingsBoardId: form.value.thingsBoardId.trim(),
            nom: form.value.nom.trim(),
            dernièreLatitude: form.value.dernièreLatitude ?? 0,
            dernièreLongitude: form.value.dernièreLongitude ?? 0,
            dernierChampId: form.value.dernierChampId,
            dernierChampNom: form.value.dernierChampNom,
            dernierClientNom: selectedClient.value ?? '',
            dernierRangOrdre: form.value.dernierRangOrdre,
            désactivé: form.value.désactivé,
            cultures: form.value.cultures,
        }

        if (isEditMode.value) {
            await apiProphet.put(`prsm/${siteCS.value}/captures/capteurs/${form.value.id}`, payload)
            Notify.success('Capteur modifié avec succès.')
        } else {
            const { id: _id, ...createPayload } = payload
            await apiProphet.post(`prsm/${siteCS.value}/captures/capteurs`, { ...createPayload, id: '' })
            Notify.success('Capteur créé avec succès.')
        }

        showDialog.value = false
        await getData()
    } catch (e) {
        notifyError(e)
    } finally {
        isSavingDialog.value = false
    }
}

// ── Lifecycle ────────────────────────────────────────────────────────────
onMounted(() => getData())
watch(siteCS, () => getData())

// ── Reorder / Rang timeline ──────────────────────────────────────────────
const activeTab = ref<'liste' | 'ordre'>('liste')
const reorderList = ref<MDBPrismeCaptureCapteur[]>([])
const reorderDirty = ref(false)
const isSavingReorder = ref(false)
let dragFromIdx = -1
const dragOverIdx = ref(-1)

// Items that need saving — tracked individually so we only PUT what changed.
const pendingRankUpdates = ref<Map<string, number>>(new Map())

watch(activeTab, (tab) => {
    if (tab === 'ordre') {
        reorderList.value = [...capteurs.value].filter(c => !c.désactivé).sort((a, b) => a.dernierRangOrdre - b.dernierRangOrdre)
        reorderDirty.value = false
        pendingRankUpdates.value = new Map()
    }
})

function onDragStart(index: number) {
    dragFromIdx = index
}

function onDragOver(index: number) {
    if (dragFromIdx === -1 || dragFromIdx === index) return
    dragOverIdx.value = index
    const list = [...reorderList.value]
    const [moved] = list.splice(dragFromIdx, 1)
    list.splice(index, 0, moved)
    reorderList.value = list
    dragFromIdx = index
}

function onDragEnd() {
    if (dragFromIdx !== -1) return // still dragging
    dragOverIdx.value = -1
}

// Called when the browser fires dragend (mouse released)
function onDragFinished(finalIdx: number) {
    dragFromIdx = -1
    dragOverIdx.value = -1

    const list = reorderList.value
    const moved = list[finalIdx]
    if (!moved) return

    const prevRank = finalIdx > 0 ? list[finalIdx - 1].dernierRangOrdre : 0
    const nextRank = finalIdx < list.length - 1 ? list[finalIdx + 1].dernierRangOrdre : (list[finalIdx - 1]?.dernierRangOrdre ?? 0) + 200

    const gap = nextRank - prevRank

    if (gap > 1) {
        // Plenty of room — just bisect, touch only this item
        const newRank = Math.floor(prevRank + gap / 2)
        reorderList.value[finalIdx] = { ...moved, dernierRangOrdre: newRank }
        pendingRankUpdates.value.set(moved.id, newRank)
    } else {
        // No room — renormalize the whole list to 100, 200, 300…
        reorderList.value = list.map((c, i) => ({ ...c, dernierRangOrdre: (i + 1) * 100 }))
        for (const item of reorderList.value) {
            const original = capteurs.value.find(c => c.id === item.id)
            if (!original || original.dernierRangOrdre !== item.dernierRangOrdre)
                pendingRankUpdates.value.set(item.id, item.dernierRangOrdre)
        }
    }

    reorderDirty.value = pendingRankUpdates.value.size > 0
}

async function saveReorder() {
    isSavingReorder.value = true
    try {
        const toSave = reorderList.value.filter(c => pendingRankUpdates.value.has(c.id))
        await Promise.all(toSave.map(c =>
            apiProphet.put(`prsm/${siteCS.value}/captures/capteurs/${c.id}`, c)
        ))
        Notify.success(`Ordre enregistré (${toSave.length} mise${toSave.length > 1 ? 's' : ''} à jour).`)
        pendingRankUpdates.value = new Map()
        reorderDirty.value = false
        await getData()
        reorderList.value = [...capteurs.value].filter(c => !c.désactivé).sort((a, b) => a.dernierRangOrdre - b.dernierRangOrdre)
    } catch (e) {
        notifyError(e)
    } finally {
        isSavingReorder.value = false
    }
}

// ── Table headers ─────────────────────────────────────────────────────────
const headers = [
    { title: 'Nom', key: 'nom', sortable: true },
    { title: 'Ferme', key: 'dernierClientNom', sortable: true },
    { title: 'Champ', key: 'dernierChampNom', sortable: true },
    { title: 'Rang', key: 'dernierRangOrdre', sortable: true },
    { title: 'Cultures', key: 'cultures', sortable: false },
    { title: 'État', key: 'désactivé', sortable: true },
    { title: 'Actions', key: 'actions', sortable: false, align: 'end' as const },
]
</script>

<template>
    <DialogConfirm ref="dialogConfirm"/>

    <v-card class="mx-4 my-4 page-card" rounded="xl" :loading="isLoading"
        style="height: 85vh; display: flex; flex-direction: column;">

        <!-- ── Page header ─────────────────────────────────────────────── -->
        <div class="page-header px-5 pt-5 pb-3">
            <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">

                <!-- Left: icon + title -->
                <div class="d-flex align-center" style="gap: 12px;">
                    <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
                        <v-icon icon="mdi-antenna" size="26"/>
                    </v-avatar>
                    <div>
                        <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Capteurs de spores</div>
                        <div class="text-caption text-medium-emphasis">Gestion des capteurs Prisme</div>
                    </div>
                </div>

                <!-- Right: add button -->
                <div class="d-flex align-center" style="gap: 8px;">
                    <v-btn
                        v-if="canEdit"
                        color="primary"
                        variant="tonal"
                        rounded="pill"
                        prepend-icon="mdi-plus"
                        :disabled="isLoading"
                        @click="openCreate">
                        Ajouter
                    </v-btn>
                </div>
            </div>

            <!-- Second row: filter -->
            <div class="d-flex align-center flex-wrap mt-3" style="gap: 8px;">
                <v-text-field
                    v-model="filter"
                    label="Rechercher"
                    density="compact"
                    variant="outlined"
                    rounded="lg"
                    hide-details
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    style="max-width: 300px;"/>
                <v-chip
                    :color="showDésactivés ? 'warning' : 'default'"
                    :variant="showDésactivés ? 'flat' : 'tonal'"
                    prepend-icon="mdi-eye-off-outline"
                    size="small"
                    label
                    style="cursor: pointer;"
                    @click="showDésactivés = !showDésactivés">
                    Afficher les désactivés
                </v-chip>
            </div>
        </div>

        <v-divider/>

        <!-- ── Tabs ─────────────────────────────────────────────────────── -->
        <v-tabs v-model="activeTab" density="compact" class="px-2 pt-1" color="primary">
            <v-tab value="liste" prepend-icon="mdi-format-list-bulleted">Liste</v-tab>
            <v-tab value="ordre" prepend-icon="mdi-sort-numeric-ascending">Ordre / Rang</v-tab>
        </v-tabs>

        <v-divider/>

        <!-- ── Content ──────────────────────────────────────────────────── -->
        <div style="flex: 1 1 auto; overflow: auto;">
            <v-window v-model="activeTab">
            <v-window-item value="liste">
            <v-data-table
                :headers="headers"
                :items="filteredCapteurs"
                :hide-default-header="display.smAndDown.value"
                item-value="id"
                density="comfortable"
                hover>

                <!-- Mobile card rows -->
                <template v-if="display.smAndDown.value" #item="{ item }">
                    <div class="capteur-mobile-card ma-2 pa-3">
                        <div class="d-flex align-center justify-space-between mb-2">
                            <div style="min-width: 0;">
                                <div class="d-flex align-center flex-wrap" style="gap: 6px;">
                                    <span class="text-subtitle-2 font-weight-bold text-truncate">{{ item.nom }}</span>
                                    <v-chip v-if="item.désactivé" color="warning" size="x-small" variant="flat" prepend-icon="mdi-power-off">Désactivé</v-chip>
                                </div>
                            </div>
                            <v-btn
                                v-if="canEdit"
                                icon="mdi-pencil-outline"
                                variant="tonal"
                                color="primary"
                                size="small"
                                class="flex-shrink-0 ms-2"
                                @click="openEdit(item)"/>
                        </div>
                        <v-divider class="mb-2"/>
                        <div class="d-flex align-center" style="gap: 4px;">
                            <v-icon icon="mdi-domain" size="14" color="medium-emphasis"/>
                            <span class="text-caption text-medium-emphasis text-truncate">
                                {{ item.dernierClientNom || '—' }}
                            </span>
                        </div>
                        <div class="d-flex align-center mt-1" style="gap: 4px;">
                            <v-icon icon="mdi-sprout-outline" size="14" color="medium-emphasis"/>
                            <span class="text-caption text-medium-emphasis text-truncate">
                                {{ item.dernierChampNom || '—' }}
                            </span>
                        </div>
                        <div class="d-flex align-center flex-wrap mt-1" style="gap: 4px;">
                            <v-icon icon="mdi-leaf-circle-outline" size="14" color="medium-emphasis" class="flex-shrink-0"/>
                            <span v-if="item.cultures.length === 0" class="text-caption text-medium-emphasis">Toutes les cultures</span>
                            <template v-else>
                                <v-chip v-for="c in item.cultures" :key="c" size="x-small" color="teal" variant="tonal">{{ c }}</v-chip>
                            </template>
                        </div>
                    </div>
                </template>

                <!-- Ferme -->
                <template #item.dernierClientNom="{ item }">
                    <span class="text-caption text-medium-emphasis">
                        {{ item.dernierClientNom || '—' }}
                    </span>
                </template>

                <!-- Champ -->
                <template #item.dernierChampNom="{ item }">
                    <span class="text-caption text-medium-emphasis">
                        {{ item.dernierChampNom || '—' }}
                    </span>
                </template>

                <!-- Cultures -->
                <template #item.cultures="{ item }">
                    <span v-if="item.cultures.length === 0" class="text-caption text-medium-emphasis">Toutes</span>
                    <div v-else class="d-flex flex-wrap" style="gap: 4px;">
                        <v-chip v-for="c in item.cultures" :key="c" size="x-small" color="teal" variant="tonal">{{ c }}</v-chip>
                    </div>
                </template>

                <!-- État -->
                <template #item.désactivé="{ item }">
                    <v-chip
                        v-if="item.désactivé"
                        color="warning"
                        size="x-small"
                        variant="flat"
                        prepend-icon="mdi-power-off">
                        Désactivé
                    </v-chip>
                    <v-chip
                        v-else
                        color="success"
                        size="x-small"
                        variant="tonal"
                        prepend-icon="mdi-check-circle-outline">
                        Actif
                    </v-chip>
                </template>

                <!-- Actions -->
                <template #item.actions="{ item }">
                    <v-btn
                        v-if="canEdit"
                        icon="mdi-pencil-outline"
                        variant="text"
                        size="small"
                        color="primary"
                        @click="openEdit(item)"/>
                </template>

                <!-- Empty state -->
                <template #no-data>
                    <div class="text-center py-8 text-medium-emphasis">
                        <v-icon icon="mdi-antenna" size="40" class="mb-2"/>
                        <div>Aucun capteur trouvé</div>
                    </div>
                </template>
            </v-data-table>
            </v-window-item>

            <!-- ── Ordre / Rang timeline ─────────────────────────────── -->
            <v-window-item value="ordre">
                <div class="pa-4 pa-sm-6">
                    <!-- Unsaved banner -->
                    <v-alert
                        v-if="reorderDirty"
                        type="info"
                        variant="tonal"
                        density="compact"
                        rounded="lg"
                        class="mb-4">
                        <template #default>
                            Ordre modifié — enregistrez pour appliquer.
                        </template>
                        <template #append>
                            <v-btn
                                v-if="canEdit"
                                color="primary"
                                variant="flat"
                                size="small"
                                rounded="pill"
                                prepend-icon="mdi-content-save-outline"
                                :loading="isSavingReorder"
                                @click="saveReorder">
                                Enregistrer
                            </v-btn>
                        </template>
                    </v-alert>

                    <div class="text-caption text-medium-emphasis mb-5 d-flex align-center" style="gap: 4px;">
                        <v-icon icon="mdi-cursor-move" size="14"/>
                        Glissez les capteurs pour modifier leur rang
                    </div>

                    <!-- Timeline strip -->
                    <div
                        class="reorder-track"
                        :class="display.smAndDown.value ? 'reorder-track--vertical' : 'reorder-track--horizontal'">

                        <div
                            v-for="(capteur, i) in reorderList"
                            :key="capteur.id"
                            class="reorder-item"
                            :class="{ 'reorder-item--dragover': dragOverIdx === i }"
                            :draggable="canEdit"
                            @dragstart="canEdit && onDragStart(i)"
                            @dragover.prevent="canEdit && onDragOver(i)"
                            @dragend="canEdit && onDragFinished(i)">

                            <!-- Rank badge -->
                            <div class="reorder-rank">{{ i }}</div>

                            <!-- Card -->
                            <v-card
                                class="reorder-card"
                                variant="outlined"
                                rounded="lg"
                                :class="{ 'reorder-card--dragover': dragOverIdx === i }">
                                <v-card-text class="pa-2 d-flex align-center" style="gap: 6px;">
                                    <v-icon
                                        v-if="canEdit"
                                        icon="mdi-drag-vertical"
                                        color="medium-emphasis"
                                        size="20"
                                        class="drag-handle flex-shrink-0"/>
                                    <div style="min-width: 0; flex: 1;">
                                        <div class="text-body-2 font-weight-medium text-truncate">{{ capteur.nom }}</div>
                                        <div class="text-caption text-medium-emphasis text-truncate">{{ capteur.dernierClientNom || '—' }} · {{ capteur.dernierChampNom || '—' }}</div>
                                    </div>
                                </v-card-text>
                            </v-card>

                            <!-- Connector arrow (horizontal only) -->
                            <v-icon
                                v-if="i < reorderList.length - 1 && !display.smAndDown.value"
                                icon="mdi-chevron-right"
                                size="18"
                                color="medium-emphasis"/>
                        </div>
                    </div>

                    <!-- Save button at bottom -->
                    <div v-if="canEdit && reorderDirty" class="d-flex justify-end mt-5">
                        <v-btn
                            color="primary"
                            variant="flat"
                            rounded="pill"
                            prepend-icon="mdi-content-save-outline"
                            :loading="isSavingReorder"
                            @click="saveReorder">
                            Enregistrer l'ordre
                        </v-btn>
                    </div>
                </div>
            </v-window-item>

            </v-window>
        </div>
    </v-card>

    <!-- ── Create / Edit dialog ──────────────────────────────────────────── -->
    <v-dialog
        v-model="showDialog"
        :fullscreen="display.smAndDown.value"
        :max-width="display.smAndDown.value ? undefined : 640"
        scrollable>
        <v-card>
            <v-card-title class="d-flex align-center pa-4" style="gap: 10px;">
                <v-avatar color="primary" variant="tonal" size="36" rounded="lg">
                    <v-icon :icon="isEditMode ? 'mdi-pencil' : 'mdi-plus'" size="20"/>
                </v-avatar>
                <span class="text-h6">{{ isEditMode ? 'Modifier le capteur' : 'Ajouter un capteur' }}</span>
                <v-spacer/>
                <v-btn icon="mdi-close" variant="text" @click="showDialog = false"/>
            </v-card-title>

            <v-divider/>

            <v-card-text class="pa-4">
                <v-container class="pa-0">
                    <v-row>
                        <!-- Nom -->
                        <v-col cols="12">
                            <v-text-field
                                v-model="form.nom"
                                label="Nom du capteur *"
                                variant="outlined"
                                density="compact"
                                rounded="lg"
                                prepend-inner-icon="mdi-label-outline"
                                hide-details="auto"/>
                        </v-col>

                        <!-- Rang ordre -->
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model.number="form.dernierRangOrdre"
                                label="Ordre dans le rang"
                                type="number"
                                variant="outlined"
                                density="compact"
                                rounded="lg"
                                prepend-inner-icon="mdi-counter"
                                hide-details="auto"
                                min="0"/>
                        </v-col>

                        <!-- Client selector -->
                        <v-col cols="12" sm="6">
                            <v-autocomplete
                                v-model="selectedClient"
                                :items="clients"
                                label="Client (pour champ)"
                                variant="outlined"
                                density="compact"
                                rounded="lg"
                                prepend-inner-icon="mdi-account-outline"
                                hide-details="auto"
                                clearable
                                no-data-text="Aucun client trouvé"/>
                        </v-col>

                        <!-- Champ (autocomplete + saisie libre) -->
                        <v-col cols="12">
                            <v-combobox
                                v-model="champValue"
                                :items="champSuggestions"
                                :item-title="(c: MDBPrismeChamp | string) => typeof c === 'string' ? c : c.nom"
                                :item-value="(c: MDBPrismeChamp | string) => c"
                                :loading="isLoadingChamps || isLocatingNearby"
                                label="Champ"
                                variant="outlined"
                                density="compact"
                                rounded="lg"
                                :prepend-inner-icon="isLocatingNearby ? 'mdi-crosshairs-gps' : 'mdi-map-search-outline'"
                                hide-details="auto"
                                clearable
                                return-object
                                no-data-text="Tapez pour chercher ou saisir un nom libre"
                                no-filter
                                @update:search="onChampInput"
                                @update:model-value="onChampSelect">
                                <template #item="{ props, item }">
                                    <v-list-item v-bind="props" :subtitle="typeof item.raw === 'string' ? '' : item.raw.id.slice(-8)"/>
                                </template>
                            </v-combobox>
                            <div v-if="isLocatingNearby" class="text-caption text-medium-emphasis mt-1 ms-2 d-flex align-center" style="gap: 4px;">
                                <v-icon icon="mdi-crosshairs-gps" size="12"/>
                                Recherche du champ le plus proche…
                            </div>
                            <div v-else-if="form.dernierChampId" class="text-caption text-medium-emphasis mt-1 ms-2">
                                ID champ: {{ form.dernierChampId }}
                            </div>
                        </v-col>

                        <!-- ThingsBoard ID -->
                        <v-col cols="12">
                            <v-text-field
                                v-model="form.thingsBoardId"
                                label="Identifiant ThingsBoard"
                                variant="outlined"
                                density="compact"
                                rounded="lg"
                                prepend-inner-icon="mdi-identifier"
                                hide-details="auto"/>
                        </v-col>

                        <!-- Cultures -->
                        <v-col cols="12">
                            <v-select
                                v-model="form.cultures"
                                :items="culturesDisponibles"
                                label="Cultures"
                                variant="outlined"
                                density="compact"
                                rounded="lg"
                                multiple
                                chips
                                closable-chips
                                prepend-inner-icon="mdi-leaf-circle-outline"
                                hide-details="auto"/>
                            <div class="text-caption text-medium-emphasis mt-1 ms-2">
                                Vide = associé à toutes les cultures
                            </div>
                        </v-col>

                        <!-- Désactivé -->
                        <v-col v-if="isEditMode" cols="12">
                            <v-switch
                                :model-value="!form.désactivé"
                                color="success"
                                hide-details
                                density="compact"
                                inset
                                @update:model-value="form.désactivé = !$event">
                                <template #label>
                                    <div class="d-flex align-center" style="gap: 6px;">
                                        <v-icon :icon="form.désactivé ? 'mdi-power-off' : 'mdi-check-circle-outline'" :color="form.désactivé ? 'warning' : 'success'" size="18"/>
                                        <span>{{ form.désactivé ? 'Capteur désactivé' : 'Capteur actif' }}</span>
                                    </div>
                                </template>
                            </v-switch>
                        </v-col>

                        <!-- ── Coordinates ─────────────────────────────── -->
                        <v-col cols="12">
                            <div class="d-flex align-center justify-space-between mb-2" style="gap: 8px;">
                                <div class="text-body-2 font-weight-medium">Coordonnées GPS</div>
                                <v-btn
                                    color="secondary"
                                    variant="tonal"
                                    size="small"
                                    rounded="pill"
                                    prepend-icon="mdi-crosshairs-gps"
                                    :loading="isLocating"
                                    @click="locateMe">
                                    Me localiser
                                </v-btn>
                            </div>

                            <!-- Lat / Lng manual inputs -->
                            <v-row dense class="mb-3">
                                <v-col cols="6">
                                    <v-text-field
                                        v-model.number="form.dernièreLatitude"
                                        label="Latitude"
                                        type="number"
                                        variant="outlined"
                                        density="compact"
                                        rounded="lg"
                                        hide-details="auto"
                                        step="0.000001"/>
                                </v-col>
                                <v-col cols="6">
                                    <v-text-field
                                        v-model.number="form.dernièreLongitude"
                                        label="Longitude"
                                        type="number"
                                        variant="outlined"
                                        density="compact"
                                        rounded="lg"
                                        hide-details="auto"
                                        step="0.000001"/>
                                </v-col>
                            </v-row>

                            <!-- Mapbox picker -->
                            <div class="map-container" style="position: relative; border-radius: 12px; overflow: hidden;">
                                <div class="text-caption text-medium-emphasis map-hint pa-1">
                                    <v-icon icon="mdi-gesture-tap" size="14"/> Cliquez sur la carte pour placer le capteur
                                </div>
                                <v-btn
                                    :color="showChampsOverlay ? 'success' : 'surface'"
                                    :variant="showChampsOverlay ? 'flat' : 'elevated'"
                                    size="x-small"
                                    rounded="pill"
                                    :prepend-icon="showChampsOverlay ? 'mdi-layers' : 'mdi-layers-outline'"
                                    :loading="isLoadingGeoJson"
                                    elevation="3"
                                    style="position: absolute; top: 36px; right: 8px; z-index: 1;"
                                    @click.stop="toggleChampsOverlay">
                                    Champs
                                </v-btn>
                                <MapboxMap
                                    style="width: 100%; height: 260px;"
                                    :map-style="theme.global.current.value.dark
                                        ? 'mapbox://styles/mapbox/dark-v11'
                                        : 'mapbox://styles/mapbox/satellite-streets-v12'"
                                    :center="mapCenter"
                                    :zoom="11"
                                    :access-token="MAPBOX_TOKEN"
                                    @mb-click="onMapClick">
                                    <template v-if="showChampsOverlay && champsGeoJson">
                                        <MapboxLayer id="capteur-champs-fill" :options="champsFillLayerOptions"/>
                                        <MapboxLayer id="capteur-champs-outline" :options="champsOutlineLayerOptions"/>
                                        <MapboxLayer id="capteur-champs-labels" :options="champsLabelLayerOptions"/>
                                        <MapboxLayer id="capteur-champs-group-labels" :options="champsGroupLabelLayerOptions"/>
                                    </template>
                                    <MapboxMarker
                                        v-if="markerCoords"
                                        :lng-lat="markerCoords"
                                        color="#1976D2"/>
                                </MapboxMap>
                            </div>
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-text>

            <v-divider/>

            <v-card-actions class="pa-4">
                <v-spacer/>
                <v-btn variant="text" @click="showDialog = false">Annuler</v-btn>
                <v-btn
                    color="primary"
                    variant="flat"
                    rounded="pill"
                    prepend-icon="mdi-content-save-outline"
                    :loading="isSavingDialog"
                    @click="saveDialog">
                    {{ isEditMode ? 'Enregistrer' : 'Créer' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>
/* ── Page header ─────────────────────────────── */
.page-header {
    background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}

/* ── Mobile card ─────────────────────────────── */
.capteur-mobile-card {
    background: v-bind('theme.global.name.value === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)"');
    border: 1px solid v-bind('theme.global.name.value === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"');
    border-radius: 12px;
}

/* ── Map hint overlay ────────────────────────── */
.map-container {
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.map-hint {
    position: absolute;
    top: 6px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    border-radius: 20px;
    pointer-events: none;
    z-index: 10;
    white-space: nowrap;
}

/* ── Reorder timeline ────────────────────────── */
.reorder-track--horizontal {
    display: flex;
    align-items: flex-start;
    overflow-x: auto;
    padding-bottom: 8px;
    gap: 0;
}

.reorder-track--vertical {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.reorder-item {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    transition: opacity 0.15s;
}

.reorder-item--dragover {
    opacity: 0.5;
}

.reorder-rank {
    min-width: 22px;
    height: 22px;
    border-radius: 50%;
    background: rgba(var(--v-theme-primary), 0.15);
    color: rgb(var(--v-theme-primary));
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 600;
    flex-shrink: 0;
}

.reorder-card {
    min-width: 140px;
    max-width: 200px;
}

.reorder-card--dragover {
    border-color: rgb(var(--v-theme-primary)) !important;
    background: rgba(var(--v-theme-primary), 0.08) !important;
}

.drag-handle {
    cursor: grab;
    opacity: 0.5;
}

.drag-handle:active {
    cursor: grabbing;
}
</style>
